import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { getDb } from '@/db';
import { attributionTouch } from '@/db/app.schema';
import { authApiMiddleware } from '@/middlewares/auth-middleware';

const MAX_TOUCH_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;

const optionalValueSchema = z.string().trim().min(1).max(500).optional();
const utmParamsSchema = z
  .record(
    z
      .string()
      .regex(/^utm_[a-z0-9_]+$/)
      .max(64),
    z.string().max(500)
  )
  .refine((value) => Object.keys(value).length <= 20);
const touchSchema = z.object({
  touchId: z.string().min(1).max(64),
  capturedAt: z.number().int().positive(),
  landingPath: z.string().startsWith('/').max(1000),
  referrer: z.string().url().max(1000).optional(),
  gclid: optionalValueSchema,
  gbraid: optionalValueSchema,
  wbraid: optionalValueSchema,
  utmParams: utmParamsSchema,
});
const inputSchema = z.object({
  touches: z.array(touchSchema).min(1).max(20),
});

export const saveAttributionTouches = createServerFn({ method: 'POST' })
  .inputValidator(inputSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const now = Date.now();
    const validTouches = data.touches.filter((touch) =>
      isRecentTouch(touch.capturedAt, now)
    );

    if (validTouches.length > 0) {
      await getDb()
        .insert(attributionTouch)
        .values(
          validTouches.map((touch) => ({
            id: touch.touchId,
            userId: context.userId,
            capturedAt: new Date(touch.capturedAt),
            landingPath: touch.landingPath,
            referrer: touch.referrer,
            gclid: touch.gclid,
            gbraid: touch.gbraid,
            wbraid: touch.wbraid,
            utmSource: touch.utmParams.utm_source,
            utmMedium: touch.utmParams.utm_medium,
            utmCampaign: touch.utmParams.utm_campaign,
            utmTerm: touch.utmParams.utm_term,
            utmContent: touch.utmParams.utm_content,
            utmParams: JSON.stringify(touch.utmParams),
            createdAt: new Date(now),
          }))
        )
        .onConflictDoNothing();
    }

    return {
      acceptedTouchIds: validTouches.map((touch) => touch.touchId),
    };
  });

function isRecentTouch(capturedAt: number, now: number) {
  return (
    capturedAt >= now - MAX_TOUCH_AGE_MS &&
    capturedAt <= now + MAX_CLOCK_SKEW_MS
  );
}
